import { defineNuxtRouteMiddleware } from 'nuxt/app'
import { getMainRole } from '~/utils/other'

export default defineNuxtRouteMiddleware(async (to) => {
  // skip middleware on server and for start page, as this redirects to login
  if (process.server) {
    return
  }

  const { status, data: session, signIn, signOut } = useAuth()

  // An expired refresh token still yields a session (flagged with RefreshAccessTokenError)
  // that nuxt-auth reports as authenticated. Go to the login page right away instead of
  // rendering a page whose requests all fail and each try to sign the user out.
  const tokenHelper = useAuthToken()
  if (status.value === 'authenticated' && tokenHelper.hasRefreshError()) {
    // guest-only routes need no login: just drop the dead session and show the page
    if (guestModeRoutes.some(route => to.path.startsWith(route))) {
      await signOut({ redirect: false })
      return
    }

    await tokenHelper.handleDeadSession({ callbackUrl: to.fullPath, respectDevMode: false })
    return
  }

  if (status.value === 'authenticated') {
    // guest mode routes are only allowed when not logged in
    const isOnGuestOnlyRoute = guestModeRoutes.some(route => to.path.startsWith(route))
    if (isOnGuestOnlyRoute) {
      return navigateTo('/')
    }

    const isSelfhosted = useRuntimeConfig().public.ENVIRONMENT === 'selfhosted'
    const isOnSaasOnlyAdminRoute = saasOnlyAdminRoutes.some((route: string) => to.path.startsWith(route))
    if (isSelfhosted && isOnSaasOnlyAdminRoute) {
      return navigateTo('/')
    }

    // @ts-expect-error - user roles are not part of the default session type
    const mainRole = getMainRole(session?.value?.user?.roles?.['x-hasura-allowed-roles'])
    if (mainRole === 'client') {
      const isOnAdminOnlyRoute = adminOnlyRoutes.some(route => to.path.startsWith(route))
      const isOnSpOnlyRoute = spOnlyRoutes.some(route => to.path.startsWith(route))

      if (isOnAdminOnlyRoute || isOnSpOnlyRoute) {
        return navigateTo('/')
      }
    } else if (mainRole === 'serviceprovider') {
      const isOnAdminOnlyRoute = adminOnlyRoutes.some(route => to.path.startsWith(route))

      if (isOnAdminOnlyRoute) {
        return navigateTo('/')
      }
    }
  } else {
    // not logged in, do not redirect as it affects keycloak redirect url
    const isOnGuestOnlyRoute = guestModeRoutes.some(route => to.path.startsWith(route))
    if (!isOnGuestOnlyRoute) {
      await signIn('keycloak', { callbackUrl: to.fullPath })
    }
  }
})

const adminOnlyRoutes = ['/admin']
const spOnlyRoutes = ['/provider']
// SaaS-only admin pages (cross-instance billing/usage data, internal MS Graph explorer) must never be reachable on selfhosted deployments
const saasOnlyAdminRoutes = ['/admin/graph-explorer', '/admin/selfhosted', '/admin/billing']
const guestModeRoutes = ['/meta/quickcheck/installer'] // routes that are only accessible when not logged in
